import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { MultiSelect } from './ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { format, parseISO } from 'date-fns';

export const formatCustomFieldDisplay = (field, value) => {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
  if (field.field_type === 'boolean') return value ? 'Sì' : 'No';
  if (field.field_type === 'multiselect') return Array.isArray(value) ? value.join(', ') : String(value);
  if (field.field_type === 'date') {
    try { return format(parseISO(String(value)), 'dd/MM/yyyy'); } catch { return String(value); }
  }
  return String(value);
};

export function CustomFieldInput({ field, value, onChange, testId }) {
  switch (field.field_type) {
    case 'number':
      return <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} onWheel={(e) => e.target.blur()} data-testid={testId} />;
    case 'date':
      return <Input type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
    case 'select':
      return (
        <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__"><span className="text-gray-400">Nessuno</span></SelectItem>
            {(field.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case 'multiselect':
      return (
        <div data-testid={testId}>
          <MultiSelect
            options={(field.options || []).map((o) => ({ value: o, label: o }))}
            selected={Array.isArray(value) ? value : []}
            onChange={onChange}
            placeholder="Seleziona..."
          />
        </div>
      );
    case 'boolean':
      return <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)} data-testid={testId} />;
    default:
      return <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  }
}

export function CustomFieldsSection({ fields, values, onChange, testIdPrefix = 'cf' }) {
  if (!fields?.length) return null;
  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid={`${testIdPrefix}-section`}>
      <h4 className="font-medium">Campi Personalizzati</h4>
      {fields.map((field) => (
        <div key={field.id} className="space-y-1.5">
          <Label>{field.name}{field.is_required && ' *'}</Label>
          <CustomFieldInput
            field={field}
            value={values?.[field.id]}
            onChange={(v) => onChange({ ...(values || {}), [field.id]: v })}
            testId={`${testIdPrefix}-input-${field.id}`}
          />
        </div>
      ))}
    </div>
  );
}


const isEmptyFilter = (f) => f === undefined || f === null || f === '' || (Array.isArray(f) && f.length === 0) ||
  (typeof f === 'object' && !Array.isArray(f) && !f.min && !f.max);

export const activeCustomFieldFilters = (filters) => Object.entries(filters || {}).filter(([, f]) => !isEmptyFilter(f));

export const matchesCustomFieldFilter = (field, value, filter) => {
  if (isEmptyFilter(filter)) return true;
  switch (field.field_type) {
    case 'select':
    case 'multiselect': {
      const vals = Array.isArray(value) ? value : (value ? [value] : []);
      return vals.some(v => filter.includes(v));
    }
    case 'boolean':
      return filter === 'yes' ? value === true : value !== true;
    case 'number': {
      if (value === undefined || value === null || value === '') return false;
      const n = Number(value);
      return (!filter.min || n >= Number(filter.min)) && (!filter.max || n <= Number(filter.max));
    }
    case 'date': {
      if (!value) return false;
      const d = String(value).slice(0, 10);
      return (!filter.min || d >= filter.min) && (!filter.max || d <= filter.max);
    }
    default:
      return String(value ?? '').toLowerCase().includes(String(filter).toLowerCase());
  }
};

export function CustomFieldFilter({ field, value, onChange }) {
  const testId = `cf-filter-${field.id}`;
  if (field.field_type === 'select' || field.field_type === 'multiselect') {
    return (
      <div className="w-[200px]" data-testid={testId}>
        <MultiSelect options={(field.options || []).map(o => ({ value: o, label: o }))} selected={value || []} onChange={onChange} placeholder={field.name} />
      </div>
    );
  }
  if (field.field_type === 'boolean') {
    return (
      <div className="w-[200px]">
        <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder={field.name} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{field.name}: tutti</SelectItem>
            <SelectItem value="yes">{field.name}: Sì</SelectItem>
            <SelectItem value="no">{field.name}: No</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.field_type === 'number' || field.field_type === 'date') {
    const v = value || {};
    return (
      <div className="flex items-center gap-1 border rounded-md px-2 h-9 bg-white" data-testid={testId}>
        <span className="text-xs text-gray-500 whitespace-nowrap">{field.name}</span>
        <Input type={field.field_type} value={v.min || ''} onChange={(e) => onChange({ ...v, min: e.target.value })} placeholder="da" className="h-7 w-[120px] border-0 px-1 text-xs" data-testid={`${testId}-min`} />
        <span className="text-gray-400">-</span>
        <Input type={field.field_type} value={v.max || ''} onChange={(e) => onChange({ ...v, max: e.target.value })} placeholder="a" className="h-7 w-[120px] border-0 px-1 text-xs" data-testid={`${testId}-max`} />
      </div>
    );
  }
  return (
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.name} className="w-[200px] h-9" data-testid={testId} />
  );
}
